import React, { useState } from "react";
import { Mail, MapPin, Send, CheckCircle2 } from "lucide-react";
import { siteInfo } from "../data/mock";
import { useToast } from "../hooks/use-toast";

const Contact = () => {
  const { toast } = useToast();
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({
        title: "Please fill all required fields",
        description: "Name, email and message are required.",
      });
      return;
    }
    setLoading(true);
    try {
      const data = new FormData();
      data.append("name", form.name);
      data.append("email", form.email);
      data.append("subject", form.subject || `New message from ${form.name}`);
      data.append("message", form.message);
      data.append("_subject", `LRRC site: ${form.subject || "New inquiry"} (${form.name})`);
      data.append("_template", "table");
      data.append("_captcha", "false");
      data.append("_replyto", form.email);

      const res = await fetch(
        `https://formsubmit.co/ajax/${encodeURIComponent(siteInfo.email)}`,
        {
          method: "POST",
          headers: { Accept: "application/json" },
          body: data,
        }
      );

      // Also keep a local backup
      const stored = JSON.parse(localStorage.getItem("lrrc_messages") || "[]");
      stored.push({ ...form, createdAt: new Date().toISOString() });
      localStorage.setItem("lrrc_messages", JSON.stringify(stored));

      if (res.ok) {
        setSubmitted(true);
        toast({
          title: "Message sent",
          description: "Thanks \u2014 I'll get back to you shortly.",
        });
        setForm({ name: "", email: "", subject: "", message: "" });
      } else {
        throw new Error("Network error");
      }
    } catch (err) {
      toast({
        title: "Couldn't send right now",
        description: `Please email ${siteInfo.email} directly.`,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-[#f4f1ec] text-black">
      <section className="pt-32 md:pt-40 pb-10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12">
          <span className="text-xs uppercase tracking-[0.22em] text-neutral-500">
            Contact
          </span>
          <h1
            className="mt-4 text-4xl md:text-6xl lg:text-7xl tracking-tight font-medium leading-[1.05]"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Start a project.
          </h1>
          <p className="mt-6 text-neutral-700 max-w-2xl text-lg leading-relaxed">
            For original scoring, sound design, audio direction, or general
            inquiries — fill out the form below or reach out directly.
          </p>
        </div>
      </section>

      <section className="pb-28">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-12 grid grid-cols-1 md:grid-cols-5 gap-10 md:gap-16">
          <div className="md:col-span-2 space-y-8">
            <div>
              <span className="text-xs uppercase tracking-[0.22em] text-neutral-500">
                Email
              </span>
              <a
                href={`mailto:${siteInfo.email}`}
                className="mt-2 flex items-center gap-3 text-lg md:text-xl tracking-tight hover:underline"
              >
                <Mail className="w-5 h-5" /> {siteInfo.email}
              </a>
            </div>
            <div>
              <span className="text-xs uppercase tracking-[0.22em] text-neutral-500">
                Based in
              </span>
              <p className="mt-2 flex items-center gap-3 text-lg md:text-xl tracking-tight">
                <MapPin className="w-5 h-5" /> {siteInfo.location}
              </p>
            </div>
            <div>
              <span className="text-xs uppercase tracking-[0.22em] text-neutral-500">
                Response time
              </span>
              <p className="mt-2 text-neutral-700">
                Typically within 1–3 business days.
              </p>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            className="md:col-span-3 bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-black/5"
          >
            {submitted ? (
              <div className="flex flex-col items-center text-center py-10">
                <CheckCircle2 className="w-12 h-12 text-emerald-600" />
                <h3
                  className="mt-5 text-2xl md:text-3xl font-medium"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  Message sent
                </h3>
                <p className="mt-2 text-neutral-600">
                  Thanks for reaching out. I&apos;ll be in touch soon.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 px-6 py-3 rounded-full bg-black text-white text-sm font-medium hover:bg-neutral-800"
                >
                  Send another
                </button>
              </div>
            ) : (
              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <Field
                    label="Name"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    required
                  />
                  <Field
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    required
                  />
                </div>
                <Field
                  label="Subject"
                  name="subject"
                  value={form.subject}
                  onChange={handleChange}
                />
                <div>
                  <label className="block text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2">
                    Message *
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    rows={6}
                    required
                    className="w-full bg-transparent border-b border-black/20 focus:border-black outline-none py-2 text-base resize-none transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-black text-white text-sm font-medium hover:bg-neutral-800 transition-colors disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Send Message"}{" "}
                  <Send className="w-4 h-4" />
                </button>
              </div>
            )}
          </form>
        </div>
      </section>
    </div>
  );
};

const Field = ({ label, name, value, onChange, type = "text", required }) => (
  <div>
    <label className="block text-xs uppercase tracking-[0.18em] text-neutral-500 mb-2">
      {label} {required && "*"}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full bg-transparent border-b border-black/20 focus:border-black outline-none py-2 text-base transition-colors"
    />
  </div>
);

export default Contact;
